// Soft sanity check for the "supplement amount requested" inputs.
//
// Sep 15, 2026 — Greg, via Shannon: on 23 deals the number typed into
// supplement_invoice_amount was Apex's ENTIRE rebuilt estimate (the
// "Replacement Cost Value" at the bottom of the Apex PDF = original scope +
// supplemented items) instead of the ADDED amount the field means. Nemetz:
// carrier RCV $22,003.89, typed $31,902.78 (the Apex total), real ask
// ≈ $9,898.89. The system stores the delta — the approved field always has
// been entered that way — so a request that is a large fraction of the
// carrier's RCV is almost always a total.
//
// Threshold: the 72 cleanly entered deals sit at a median 22–41% of RCV; the
// 23 mis-typed ones at 81–240%. 70% is the gap. A legitimate ask CAN exceed it
// (partial approvals — one approved supplement was 87% of RCV), so this only
// WARNS and offers the difference; it never blocks a save.

export const SUPPLEMENT_TOTAL_SUSPECT_RATIO = 0.7;

export interface SupplementEntryWarningInfo {
  /** Entered amount as a % of the carrier RCV, rounded. */
  pct: number;
  /** entered − rcv when positive: the likely intended added amount. */
  suggest: number | null;
  /** One-line explanation for tooltips / plain-text surfaces. */
  message: string;
}

const money = (n: number) =>
  "$" + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function supplementEntryWarning(entered: number, rcv: number): SupplementEntryWarningInfo | null {
  if (!(entered > 0) || !(rcv > 0)) return null;
  if (entered < SUPPLEMENT_TOTAL_SUSPECT_RATIO * rcv) return null;
  const pct = Math.round((entered / rcv) * 100);
  const diff = Math.round((entered - rcv) * 100) / 100;
  const suggest = diff > 0 ? diff : null;
  const message =
    suggest != null
      ? `${money(entered)} is ${pct}% of the carrier's RCV (${money(rcv)}). If that is Apex's revised estimate total, the added amount is ${money(suggest)}.`
      : `${money(entered)} is ${pct}% of the carrier's RCV (${money(rcv)}). A supplement request is usually a fraction of the original estimate — if that is Apex's revised total, read the added amount off the Apex document.`;
  return { pct, suggest, message };
}
